<template>
  <div class="card">
    <div class="card-body">
      <h5 style="color: blue; margin-bottom: 20px">
        {{ $t("teacherAttendance.title") }}
      </h5>

      <a-alert
        v-if="!teacherAttendanceEnabled"
        type="warning"
        show-icon
        class="mb-3"
        :message="$t('teacherAttendance.moduleDisabled')"
      />

      <template v-else>
      <a-alert
        v-if="requireAttendanceValidation"
        type="info"
        show-icon
        class="mb-3"
        :message="$t('teacherAttendance.validationHint')"
      />

      <a-tabs v-model="activeTab" @change="onTabChange">
        <a-tab-pane key="daily" :tab="$t('teacherAttendance.tabDaily')">
          <!-- Filters -->
          <a-row :gutter="16" class="mb-3">
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{ $t("recette.date") }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="selectedDate"
                :disabledDate="(c) => c > moment()"
                @change="handleDateChange"
              />
            </a-col>
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.filterTeacher")
              }}</label>
              <a-select
                show-search
                allow-clear
                :placeholder="$t('teacherAttendance.allTeachers')"
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 100%"
                v-model="selectedTeacher"
                @change="loadAttendance"
              >
                <a-select-option
                  v-for="t in teachers"
                  :key="t._id"
                  :value="t._id"
                >
                  {{ teacherName(t) }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.filterStatus")
              }}</label>
              <a-select
                allow-clear
                :placeholder="$t('teacherAttendance.allStatuses')"
                style="width: 100%"
                v-model="filterStatus"
                @change="applyFilter"
              >
                <a-select-option value="present">{{
                  $t("assiduite.present")
                }}</a-select-option>
                <a-select-option value="absent">{{
                  $t("assiduite.absence")
                }}</a-select-option>
                <a-select-option value="late">{{
                  $t("assiduite.retard")
                }}</a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="8" :md="6" style="padding-top: 22px">
              <a-checkbox v-model="showDeleted" @change="loadAttendance">
                {{ $t("teacherAttendance.showDeleted") }}
              </a-checkbox>
            </a-col>
            <a-col
              v-if="requireAttendanceValidation"
              :xs="24"
              :sm="8"
              :md="6"
              style="padding-top: 22px"
            >
              <a-button
                type="primary"
                ghost
                :loading="validateDayLoading"
                @click="validateSessionDay"
              >
                {{ $t("teacherAttendance.validateAll") }}
              </a-button>
            </a-col>
          </a-row>

          <!-- Sessions table -->
          <a-table
            :loading="loading"
            :data-source="filteredData"
            :columns="columns"
            :pagination="{ pageSize: 20 }"
            row-key="_id"
          >
            <template slot="teacherName" slot-scope="text, record">
              {{ teacherName(record.teacher) }}
            </template>

            <template slot="session" slot-scope="text, record">
              <span v-if="record.schedule">
                {{ record.schedule.name || sessionLabel(record.schedule) }}
                <span class="text-muted" style="font-size: 12px">
                  ({{ record.schedule.time }} - {{ record.schedule.ends }})
                </span>
              </span>
            </template>

            <template slot="status" slot-scope="text, record">
              <a-tag
                v-if="record.attendance"
                :color="statusColor(record.attendance.status)"
              >
                {{
                  $t("teacherAttendance.status_" + record.attendance.status)
                }}
                <span v-if="record.attendance.status === 'late'">
                  ({{ record.attendance.delayMinutes }} min)
                </span>
              </a-tag>
              <a-tag v-else color="blue">
                {{ $t("teacherAttendance.notMarked") }}
              </a-tag>
              <a-tooltip
                v-if="record.attendance && record.attendance.flagOffSite"
                :title="$t('teacherAttendance.offSiteHint')"
              >
                <a-tag color="red" style="margin-left: 4px">
                  <a-icon type="warning" />
                  {{ $t("teacherAttendance.offSite") }}
                </a-tag>
              </a-tooltip>
              <a-tag
                v-if="record.attendance && record.attendance.deletedAt"
                color="red"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.recordDeleted") }}
              </a-tag>
              <a-tag
                v-if="
                  requireAttendanceValidation &&
                    record.attendance &&
                    !record.attendance.deletedAt &&
                    record.attendance.validationStatus === 'pending'
                "
                color="orange"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.validationPending") }}
              </a-tag>
              <a-tag
                v-else-if="
                  requireAttendanceValidation &&
                    record.attendance &&
                    !record.attendance.deletedAt &&
                    record.attendance.validationStatus === 'validated'
                "
                color="green"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.validationValidated") }}
              </a-tag>
            </template>

            <template slot="actions" slot-scope="text, record">
              <a-button-group size="small">
                <a-button
                  type="primary"
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'present'
                  "
                  @click="markStatus(record, 'present')"
                >
                  {{ $t("assiduite.present") }}
                </a-button>
                <a-button
                  type="danger"
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'absent'
                  "
                  @click="markStatus(record, 'absent')"
                >
                  {{ $t("assiduite.absence") }}
                </a-button>
                <a-button
                  style="background: #faad14; border-color: #faad14; color: #fff"
                  @click="openLateModal(record)"
                >
                  {{ $t("assiduite.retard") }}
                </a-button>
                <a-button
                  v-if="
                    requireAttendanceValidation &&
                      record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.validationStatus === 'pending'
                  "
                  type="primary"
                  ghost
                  @click="validateSessionAttendance(record)"
                >
                  {{ $t("teacherAttendance.validate") }}
                </a-button>
                <a-button
                  v-if="record.attendance"
                  @click="openHistory(record.attendance._id, 'session')"
                >
                  <a-icon type="history" />
                </a-button>
                <a-button
                  v-if="record.attendance && !record.attendance.deletedAt"
                  @click="removeAttendance(record)"
                >
                  <a-icon type="delete" />
                </a-button>
              </a-button-group>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane
          key="personnelDaily"
          :tab="$t('teacherAttendance.tabPersonnelDaily')"
        >
          <a-alert
            type="info"
            show-icon
            class="mb-3"
            :message="$t('teacherAttendance.personnelDailyHint')"
          />
          <a-row :gutter="16" class="mb-3">
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{ $t("recette.date") }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="personnelDate"
                :disabledDate="(c) => c > moment()"
                @change="loadPersonnelDay"
              />
            </a-col>
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.filterPersonnel")
              }}</label>
              <a-select
                show-search
                allow-clear
                :placeholder="$t('teacherAttendance.allPersonnel')"
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 100%"
                v-model="personnelFilterEmployee"
                @change="applyPersonnelFilter"
              >
                <a-select-option
                  v-for="e in personnelEmployees"
                  :key="e._id"
                  :value="e._id"
                >
                  {{ employeeName(e) }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.filterStatus")
              }}</label>
              <a-select
                allow-clear
                :placeholder="$t('teacherAttendance.allStatuses')"
                style="width: 100%"
                v-model="personnelFilterStatus"
                @change="applyPersonnelFilter"
              >
                <a-select-option value="present">{{
                  $t("assiduite.present")
                }}</a-select-option>
                <a-select-option value="absent">{{
                  $t("assiduite.absence")
                }}</a-select-option>
                <a-select-option value="late">{{
                  $t("assiduite.retard")
                }}</a-select-option>
                <a-select-option value="absent_morning">{{
                  $t("teacherAttendance.status_absent_morning")
                }}</a-select-option>
                <a-select-option value="absent_afternoon">{{
                  $t("teacherAttendance.status_absent_afternoon")
                }}</a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="8" :md="6" style="padding-top: 22px">
              <a-checkbox
                v-model="showDeletedPersonnel"
                @change="loadPersonnelDay"
              >
                {{ $t("teacherAttendance.showDeleted") }}
              </a-checkbox>
            </a-col>
            <a-col
              v-if="requireAttendanceValidation"
              :xs="24"
              :sm="8"
              :md="6"
              style="padding-top: 22px"
            >
              <a-button
                type="primary"
                ghost
                :loading="validatePersonnelDayLoading"
                @click="validatePersonnelDay"
              >
                {{ $t("teacherAttendance.validateAll") }}
              </a-button>
            </a-col>
          </a-row>

          <a-table
            :loading="personnelLoading"
            :data-source="personnelFilteredRows"
            :columns="personnelColumns"
            :pagination="{ pageSize: 20 }"
            row-key="rowKey"
          >
            <template slot="personnelName" slot-scope="text, record">
              {{ employeeName(record.employee) }}
              <a-tag
                v-if="record.employee && record.employee.isA_Teacher"
                color="blue"
                style="margin-left: 6px"
              >
                {{ $t("personnel.enseignant") }}
              </a-tag>
              <a-tag v-else color="green" style="margin-left: 6px">
                {{ $t("personnel.personnel") }}
              </a-tag>
            </template>

            <template slot="personnelStatus" slot-scope="text, record">
              <a-tag
                v-if="record.attendance"
                :color="statusColor(record.attendance.status)"
              >
                {{
                  $t(
                    "teacherAttendance.status_" + record.attendance.status
                  )
                }}
                <span v-if="record.attendance.status === 'late'">
                  ({{ record.attendance.delayMinutes }} min)
                </span>
              </a-tag>
              <a-tag v-else color="cyan">
                {{ $t("teacherAttendance.defaultPresent") }}
              </a-tag>
              <a-tag
                v-if="record.attendance && record.attendance.deletedAt"
                color="red"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.recordDeleted") }}
              </a-tag>
              <a-tag
                v-if="
                  requireAttendanceValidation &&
                    record.attendance &&
                    !record.attendance.deletedAt &&
                    record.attendance.validationStatus === 'pending'
                "
                color="orange"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.validationPending") }}
              </a-tag>
              <a-tag
                v-else-if="
                  requireAttendanceValidation &&
                    record.attendance &&
                    !record.attendance.deletedAt &&
                    record.attendance.validationStatus === 'validated'
                "
                color="green"
                style="margin-left: 4px"
              >
                {{ $t("teacherAttendance.validationValidated") }}
              </a-tag>
            </template>

            <template slot="personnelActions" slot-scope="text, record">
              <a-button-group size="small">
                <a-button
                  type="primary"
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'present'
                  "
                  @click="markPersonnelStatus(record, 'present')"
                >
                  {{ $t("assiduite.present") }}
                </a-button>
                <a-button
                  type="danger"
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'absent'
                  "
                  @click="markPersonnelStatus(record, 'absent')"
                >
                  {{ $t("assiduite.absence") }}
                </a-button>
                <a-button
                  style="background: #faad14; border-color: #faad14; color: #fff"
                  @click="openPersonnelLateModal(record)"
                >
                  {{ $t("assiduite.retard") }}
                </a-button>
                <a-button
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'absent_morning'
                  "
                  @click="markPersonnelStatus(record, 'absent_morning')"
                >
                  {{ $t("teacherAttendance.status_absent_morning_short") }}
                </a-button>
                <a-button
                  :disabled="
                    record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.status === 'absent_afternoon'
                  "
                  @click="markPersonnelStatus(record, 'absent_afternoon')"
                >
                  {{ $t("teacherAttendance.status_absent_afternoon_short") }}
                </a-button>
                <a-button
                  v-if="
                    requireAttendanceValidation &&
                      record.attendance &&
                      !record.attendance.deletedAt &&
                      record.attendance.validationStatus === 'pending'
                  "
                  type="primary"
                  ghost
                  @click="validatePersonnelAttendance(record)"
                >
                  {{ $t("teacherAttendance.validate") }}
                </a-button>
                <a-button
                  v-if="record.attendance"
                  @click="openHistory(record.attendance._id, 'personnel')"
                >
                  <a-icon type="history" />
                </a-button>
                <a-button
                  v-if="record.attendance && !record.attendance.deletedAt"
                  @click="removePersonnelAttendance(record)"
                >
                  <a-icon type="delete" />
                </a-button>
              </a-button-group>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="report" :tab="$t('teacherAttendance.tabReport')">
          <a-row :gutter="16" class="mb-3" type="flex" align="bottom">
            <a-col :xs="24" :sm="12" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.reportMode")
              }}</label>
              <a-radio-group
                v-model="reportMode"
                button-style="solid"
                @change="onReportModeChange"
              >
                <a-radio-button value="session">
                  {{ $t("teacherAttendance.tabDaily") }}
                </a-radio-button>
                <a-radio-button value="personnelDaily">
                  {{ $t("teacherAttendance.tabPersonnelDaily") }}
                </a-radio-button>
              </a-radio-group>
            </a-col>
            <a-col :xs="24" :sm="8" :md="4">
              <label class="filter-label">{{
                $t("etablissement.dateDebut")
              }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="reportStart"
                :disabledDate="
                  (c) =>
                    c > moment() || (reportEnd && c > reportEnd)
                "
              />
            </a-col>
            <a-col :xs="24" :sm="8" :md="4">
              <label class="filter-label">{{
                $t("etablissement.dateFin")
              }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="reportEnd"
                :disabledDate="
                  (c) =>
                    c > moment() || (reportStart && c < reportStart)
                "
              />
            </a-col>
            <a-col
              v-if="reportMode === 'session'"
              :xs="24"
              :sm="8"
              :md="5"
            >
              <label class="filter-label">{{
                $t("teacherAttendance.filterTeacher")
              }}</label>
              <a-select
                show-search
                allow-clear
                :placeholder="$t('teacherAttendance.allTeachers')"
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 100%"
                v-model="reportTeacher"
              >
                <a-select-option
                  v-for="t in teachers"
                  :key="t._id"
                  :value="t._id"
                >
                  {{ teacherName(t) }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col v-else :xs="24" :sm="8" :md="5">
              <label class="filter-label">{{
                $t("teacherAttendance.filterPersonnel")
              }}</label>
              <a-select
                show-search
                allow-clear
                :placeholder="$t('teacherAttendance.allPersonnel')"
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 100%"
                v-model="reportEmployee"
              >
                <a-select-option
                  v-for="e in reportPersonnelOptions"
                  :key="e._id"
                  :value="e._id"
                >
                  {{ employeeName(e) }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="12" :md="5">
              <a-button
                type="primary"
                :loading="reportLoading"
                @click="loadSummary"
                style="margin-right: 8px"
              >
                {{ $t("action.afficher") }}
              </a-button>
              <a-button
                :disabled="!summaryRows.length"
                :loading="pdfLoading"
                @click="exportPdf"
              >
                <a-icon type="file-pdf" />
                {{ $t("action.imprimer") }}
              </a-button>
            </a-col>
          </a-row>

          <a-row
            :gutter="16"
            class="mb-3"
            v-if="summaryTotals && reportMode === 'session'"
          >
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.present") }}</div>
                <div class="stat-value">{{ summaryTotals.present }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.absence") }}</div>
                <div class="stat-value">{{ summaryTotals.absent }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.retard") }}</div>
                <div class="stat-value">{{ summaryTotals.late }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.workedHours") }}
                </div>
                <div class="stat-value">{{ summaryTotals.workedHours }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.workedDays") }}
                </div>
                <div class="stat-value">{{ summaryTotals.workedDays }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.delayMinutes") }}
                </div>
                <div class="stat-value">{{ summaryTotals.delayMinutes }}</div>
              </div>
            </a-col>
          </a-row>

          <a-row
            :gutter="16"
            class="mb-3"
            v-if="summaryTotals && reportMode === 'personnelDaily'"
          >
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.present") }}</div>
                <div class="stat-value">{{ summaryTotals.present }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.absence") }}</div>
                <div class="stat-value">{{ summaryTotals.absent }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">{{ $t("assiduite.retard") }}</div>
                <div class="stat-value">{{ summaryTotals.late }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.status_absent_morning_short") }}
                </div>
                <div class="stat-value">{{ summaryTotals.absentMorning }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.status_absent_afternoon_short") }}
                </div>
                <div class="stat-value">{{ summaryTotals.absentAfternoon }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.delayMinutes") }}
                </div>
                <div class="stat-value">{{ summaryTotals.delayMinutes }}</div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="3">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.markedDays") }}
                </div>
                <div class="stat-value">{{ summaryTotals.markedDays }}</div>
              </div>
            </a-col>
          </a-row>

          <a-table
            :loading="reportLoading"
            :data-source="summaryRows"
            :columns="activeReportColumns"
            :pagination="{ pageSize: 20 }"
            :row-key="reportMode === 'session' ? 'teacherId' : 'employeeId'"
          />
        </a-tab-pane>

        <a-tab-pane
          key="comparison"
          :tab="$t('teacherAttendance.tabComparison')"
        >
          <a-alert
            type="info"
            show-icon
            class="mb-3"
            :message="$t('teacherAttendance.comparisonHint')"
          />
          <a-row :gutter="16" class="mb-3" type="flex" align="bottom">
            <a-col :xs="24" :sm="8" :md="5">
              <label class="filter-label">{{
                $t("etablissement.dateDebut")
              }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="comparisonStart"
                :disabledDate="
                  (c) =>
                    c > moment() || (comparisonEnd && c > comparisonEnd)
                "
              />
            </a-col>
            <a-col :xs="24" :sm="8" :md="5">
              <label class="filter-label">{{
                $t("etablissement.dateFin")
              }}</label>
              <a-date-picker
                style="width: 100%"
                format="DD/MM/YYYY"
                v-model="comparisonEnd"
                :disabledDate="
                  (c) =>
                    c > moment() || (comparisonStart && c < comparisonStart)
                "
              />
            </a-col>
            <a-col :xs="24" :sm="8" :md="6">
              <label class="filter-label">{{
                $t("teacherAttendance.searchByName")
              }}</label>
              <a-input
                allow-clear
                v-model="comparisonNameSearch"
                :placeholder="$t('teacherAttendance.searchByNamePlaceholder')"
              >
                <a-icon slot="prefix" type="search" />
              </a-input>
            </a-col>
            <a-col :xs="24" :sm="12" :md="8">
              <a-button
                type="primary"
                :loading="comparisonLoading"
                @click="loadComparison"
                style="margin-right: 8px"
              >
                {{ $t("action.afficher") }}
              </a-button>
              <a-button
                :disabled="!filteredComparisonRows.length"
                :loading="comparisonPdfLoading"
                @click="exportComparisonPdf"
              >
                <a-icon type="file-pdf" />
                {{ $t("action.imprimer") }}
              </a-button>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="mb-3" v-if="filteredComparisonTotals">
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.expectedHours") }}
                </div>
                <div class="stat-value">
                  {{ filteredComparisonTotals.expectedHours }}
                </div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.actualHours") }}
                </div>
                <div class="stat-value">
                  {{ filteredComparisonTotals.actualHours }}
                </div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.absentHours") }}
                </div>
                <div class="stat-value">
                  {{ filteredComparisonTotals.absentHours }}
                </div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.delayHours") }}
                </div>
                <div class="stat-value">
                  {{ filteredComparisonTotals.delayHours }}
                </div>
              </div>
            </a-col>
            <a-col :xs="12" :sm="8" :md="4">
              <div class="stat-box">
                <div class="stat-label">
                  {{ $t("teacherAttendance.varianceHours") }}
                </div>
                <div class="stat-value">
                  {{ filteredComparisonTotals.varianceHours }}
                </div>
              </div>
            </a-col>
          </a-row>

          <a-table
            :loading="comparisonLoading"
            :data-source="filteredComparisonRows"
            :columns="comparisonColumns"
            :pagination="{ pageSize: 20 }"
            row-key="employeeId"
            :expandIcon="comparisonExpandIcon"
          >
            <template slot="expandedRowRender" slot-scope="record">
              <a-table
                v-if="record.bySubject && record.bySubject.length"
                size="small"
                :pagination="false"
                :columns="comparisonSubjectColumns"
                :data-source="record.bySubject"
                :row-key="
                  (r) => r.subjectId || r.subjectName || 'subject-none'
                "
              />
              <span v-else>{{
                $t("teacherAttendance.noSubjectBreakdown")
              }}</span>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>

      <!-- Late modal -->
      <a-modal
        :visible="lateModalVisible"
        :title="$t('teacherAttendance.lateTitle')"
        @cancel="closeLateModal"
        @ok="confirmLate"
        :ok-button-props="{ props: { disabled: !delayMinutes } }"
      >
        <p>{{ $t("teacherAttendance.enterDelay") }}</p>
        <a-input-number
          v-model="delayMinutes"
          :min="1"
          :max="480"
          style="width: 100%"
          :placeholder="$t('teacherAttendance.delayPlaceholder')"
        />
      </a-modal>

      <a-drawer
        :title="$t('teacherAttendance.editHistory')"
        :visible="historyVisible"
        :width="480"
        @close="closeHistory"
      >
        <a-spin :spinning="historyLoading">
          <a-alert
            v-if="historyData && historyData.deletedAt"
            type="warning"
            show-icon
            class="mb-3"
            :message="$t('teacherAttendance.recordDeleted')"
            :description="
              moment(historyData.deletedAt).format('DD/MM/YYYY HH:mm')
            "
          />
          <a-empty
            v-if="
              !historyLoading &&
                (!historyEntries || !historyEntries.length)
            "
            :description="$t('teacherAttendance.noHistory')"
          />
          <a-timeline v-else>
            <a-timeline-item
              v-for="(entry, idx) in historyEntries"
              :key="idx"
              :color="historyColor(entry.action)"
            >
              <div>
                <strong>{{
                  $t("teacherAttendance.historyAction_" + entry.action)
                }}</strong>
                <span class="text-muted" style="margin-left: 8px">
                  {{ moment(entry.at).format("DD/MM/YYYY HH:mm") }}
                </span>
              </div>
              <div v-if="entry.byName" style="font-size: 12px">
                {{ $t("teacherAttendance.historyBy") }}: {{ entry.byName }}
                <span v-if="entry.byModel" class="text-muted">
                  ({{ entry.byModel }})
                </span>
              </div>
              <div
                v-if="entry.before || entry.after"
                style="margin-top: 6px; font-size: 12px"
              >
                <div v-if="entry.before">
                  <span class="text-muted"
                    >{{ $t("teacherAttendance.historyBefore") }}:</span
                  >
                  {{ formatHistorySnapshot(entry.before) }}
                </div>
                <div v-if="entry.after">
                  <span class="text-muted"
                    >{{ $t("teacherAttendance.historyAfter") }}:</span
                  >
                  {{ formatHistorySnapshot(entry.after) }}
                </div>
              </div>
            </a-timeline-item>
          </a-timeline>
        </a-spin>
      </a-drawer>
      </template>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import moment from "moment";
import { notification } from "ant-design-vue";
import { showApiError } from "@/utilities/showApiError";
import {
  generateTeacherAttendanceSummaryPdf,
  generatePersonnelDailyAttendanceSummaryPdf,
  generateAttendanceComparisonPdf,
} from "./summaryPdfGenerator";

export default {
  computed: {
    ...mapState(["settings", "user"]),
    activeBuildingConfig() {
      const building = (this.user.building || []).find(
        (b) => b.dbName == this.settings.activeBuilding
      );
      return (building && building.config) || {};
    },
    teacherAttendanceEnabled() {
      const v = this.activeBuildingConfig.teacherAttendanceEnabled;
      return v === undefined || v === null ? true : !!v;
    },
    requireAttendanceValidation() {
      return (
        this.teacherAttendanceEnabled &&
        !!this.activeBuildingConfig.requireTeacherAttendanceValidation
      );
    },
    columns() {
      return [
        {
          title: this.$t("teacherAttendance.teacher"),
          key: "teacherName",
          scopedSlots: { customRender: "teacherName" },
        },
        {
          title: this.$t("emploi.seance"),
          key: "session",
          scopedSlots: { customRender: "session" },
        },
        {
          title: this.$t("assiduite.assiduite"),
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("action.actions"),
          key: "actions",
          scopedSlots: { customRender: "actions" },
          width: 350,
        },
      ];
    },
    reportColumns() {
      return [
        {
          title: this.$t("teacherAttendance.teacher"),
          dataIndex: "teacherName",
          key: "teacherName",
        },
        {
          title: this.$t("assiduite.present"),
          dataIndex: "present",
          key: "present",
        },
        {
          title: this.$t("assiduite.absence"),
          dataIndex: "absent",
          key: "absent",
        },
        {
          title: this.$t("assiduite.retard"),
          dataIndex: "late",
          key: "late",
        },
        {
          title: this.$t("teacherAttendance.delayMinutes"),
          dataIndex: "delayMinutes",
          key: "delayMinutes",
        },
        {
          title: this.$t("teacherAttendance.workedHours"),
          dataIndex: "workedHours",
          key: "workedHours",
        },
        {
          title: this.$t("teacherAttendance.workedDays"),
          dataIndex: "workedDays",
          key: "workedDays",
        },
        {
          title: this.$t("teacherAttendance.offSite"),
          dataIndex: "offSite",
          key: "offSite",
        },
      ];
    },
    personnelDailyReportColumns() {
      return [
        {
          title: this.$t("etatPaiement.personnel"),
          dataIndex: "employeeName",
          key: "employeeName",
        },
        {
          title: this.$t("assiduite.type"),
          key: "role",
          customRender: (text, record) =>
            record.isA_Teacher
              ? this.$t("personnel.enseignant")
              : this.$t("personnel.personnel"),
        },
        {
          title: this.$t("assiduite.present"),
          dataIndex: "present",
          key: "present",
        },
        {
          title: this.$t("assiduite.absence"),
          dataIndex: "absent",
          key: "absent",
        },
        {
          title: this.$t("assiduite.retard"),
          dataIndex: "late",
          key: "late",
        },
        {
          title: this.$t("teacherAttendance.status_absent_morning_short"),
          dataIndex: "absentMorning",
          key: "absentMorning",
        },
        {
          title: this.$t("teacherAttendance.status_absent_afternoon_short"),
          dataIndex: "absentAfternoon",
          key: "absentAfternoon",
        },
        {
          title: this.$t("teacherAttendance.delayMinutes"),
          dataIndex: "delayMinutes",
          key: "delayMinutes",
        },
        {
          title: this.$t("teacherAttendance.markedDays"),
          dataIndex: "markedDays",
          key: "markedDays",
        },
      ];
    },
    activeReportColumns() {
      return this.reportMode === "personnelDaily"
        ? this.personnelDailyReportColumns
        : this.reportColumns;
    },
    reportPersonnelOptions() {
      if (this.personnelEmployees.length) return this.personnelEmployees;
      return [];
    },
    filteredComparisonRows() {
      const q = (this.comparisonNameSearch || "").trim().toLowerCase();
      if (!q) return this.comparisonRows;
      return this.comparisonRows.filter((row) =>
        String(row.employeeName || "")
          .toLowerCase()
          .includes(q)
      );
    },
    filteredComparisonTotals() {
      if (!this.comparisonTotals && !this.comparisonRows.length) return null;
      const q = (this.comparisonNameSearch || "").trim();
      if (!q) return this.comparisonTotals;
      const rows = this.filteredComparisonRows;
      const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
      return rows.reduce(
        (acc, r) => {
          acc.expectedHours = round2(acc.expectedHours + r.expectedHours);
          acc.actualHours = round2(acc.actualHours + r.actualHours);
          acc.absentHours = round2(acc.absentHours + r.absentHours);
          acc.delayHours = round2(acc.delayHours + r.delayHours);
          acc.varianceHours = round2(acc.varianceHours + r.varianceHours);
          return acc;
        },
        {
          expectedHours: 0,
          actualHours: 0,
          absentHours: 0,
          delayHours: 0,
          varianceHours: 0,
        }
      );
    },
    comparisonColumns() {
      return [
        {
          title: this.$t("etatPaiement.personnel"),
          dataIndex: "employeeName",
          key: "employeeName",
        },
        {
          title: this.$t("assiduite.type"),
          key: "role",
          customRender: (text, record) =>
            record.isA_Teacher
              ? this.$t("personnel.enseignant")
              : this.$t("personnel.personnel"),
        },
        {
          title: this.$t("teacherAttendance.expectedHours"),
          dataIndex: "expectedHours",
          key: "expectedHours",
        },
        {
          title: this.$t("teacherAttendance.actualHours"),
          dataIndex: "actualHours",
          key: "actualHours",
        },
        {
          title: this.$t("teacherAttendance.absentHours"),
          dataIndex: "absentHours",
          key: "absentHours",
        },
        {
          title: this.$t("teacherAttendance.delayHours"),
          dataIndex: "delayHours",
          key: "delayHours",
        },
        {
          title: this.$t("teacherAttendance.varianceHours"),
          dataIndex: "varianceHours",
          key: "varianceHours",
        },
      ];
    },
    comparisonSubjectColumns() {
      return [
        {
          title: this.$t("emploi.matiere"),
          dataIndex: "subjectName",
          key: "subjectName",
          customRender: (text) => text || "—",
        },
        {
          title: this.$t("personnel.hoursPerWeek"),
          dataIndex: "expectedHoursPerWeek",
          key: "expectedHoursPerWeek",
        },
        {
          title: this.$t("teacherAttendance.expectedHours"),
          dataIndex: "expectedHours",
          key: "expectedHours",
        },
        {
          title: this.$t("teacherAttendance.timetableHours"),
          dataIndex: "timetableHours",
          key: "timetableHours",
        },
        {
          title: this.$t("teacherAttendance.actualHours"),
          dataIndex: "actualHours",
          key: "actualHours",
        },
        {
          title: this.$t("teacherAttendance.absentHours"),
          dataIndex: "absentHours",
          key: "absentHours",
        },
        {
          title: this.$t("teacherAttendance.delayHours"),
          dataIndex: "delayHours",
          key: "delayHours",
        },
        {
          title: this.$t("teacherAttendance.varianceHours"),
          dataIndex: "varianceHours",
          key: "varianceHours",
        },
      ];
    },
    personnelColumns() {
      return [
        {
          title: this.$t("etatPaiement.personnel"),
          key: "personnelName",
          scopedSlots: { customRender: "personnelName" },
        },
        {
          title: this.$t("assiduite.type"),
          key: "personnelStatus",
          scopedSlots: { customRender: "personnelStatus" },
        },
        {
          title: this.$t("action.actions"),
          key: "personnelActions",
          scopedSlots: { customRender: "personnelActions" },
          width: 480,
        },
      ];
    },
  },
  async created() {
    this.selectedDate = moment();
    this.personnelDate = moment();
    this.reportStart = moment().startOf("month");
    this.reportEnd = moment();
    this.comparisonStart = moment().startOf("month");
    this.comparisonEnd = moment();
    await this.loadTeachers();
    await this.handleDateChange();
  },
  data() {
    return {
      activeTab: "daily",
      loading: false,
      selectedDate: null,
      selectedTeacher: undefined,
      filterStatus: undefined,
      teachers: [],
      schedules: [],
      attendanceMap: {},
      tableData: [],
      filteredData: [],
      lateModalVisible: false,
      lateRecord: null,
      lateMode: "session",
      delayMinutes: null,
      reportStart: null,
      reportEnd: null,
      reportTeacher: undefined,
      reportEmployee: undefined,
      reportMode: "session",
      reportLoading: false,
      pdfLoading: false,
      summaryRows: [],
      summaryTotals: null,
      personnelDate: null,
      personnelLoading: false,
      personnelRows: [],
      personnelFilteredRows: [],
      personnelEmployees: [],
      personnelFilterEmployee: undefined,
      personnelFilterStatus: undefined,
      comparisonStart: null,
      comparisonEnd: null,
      comparisonLoading: false,
      comparisonPdfLoading: false,
      comparisonRows: [],
      comparisonTotals: null,
      comparisonNameSearch: "",
      validateDayLoading: false,
      validatePersonnelDayLoading: false,
      showDeleted: false,
      showDeletedPersonnel: false,
      historyVisible: false,
      historyLoading: false,
      historyData: null,
      historyEntries: [],
    };
  },
  methods: {
    moment,
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    historyColor(action) {
      if (action === "deleted") return "red";
      if (action === "created" || action === "restored") return "green";
      return "blue";
    },
    formatHistorySnapshot(snap) {
      if (!snap) return "—";
      const status = snap.status
        ? this.$t("teacherAttendance.status_" + snap.status)
        : "—";
      const delay =
        snap.status === "late" && snap.delayMinutes != null
          ? ` (${snap.delayMinutes} ${this.$t("teacherAttendance.minShort")})`
          : "";
      const source = snap.source
        ? ` · ${this.$t("teacherAttendance.source_" + snap.source)}`
        : "";
      return `${status}${delay}${source}`;
    },
    async openHistory(id, mode) {
      if (!id) return;
      this.historyVisible = true;
      this.historyLoading = true;
      this.historyData = null;
      this.historyEntries = [];
      try {
        const url =
          mode === "personnel"
            ? `/personnelDailyAttendance/${id}/history`
            : `/teacherAttendance/${id}/history`;
        const { data } = await apiClient.get(url);
        this.historyData = data;
        const entries = [...(data.editHistory || [])];
        entries.sort((a, b) => new Date(b.at) - new Date(a.at));
        this.historyEntries = entries;
      } catch (err) {
        showApiError(this, err);
        this.historyVisible = false;
      } finally {
        this.historyLoading = false;
      }
    },
    closeHistory() {
      this.historyVisible = false;
      this.historyData = null;
      this.historyEntries = [];
    },
    async validateSessionAttendance(record) {
      if (!record.attendance || !record.attendance._id) return;
      try {
        await apiClient.post(
          `/teacherAttendance/${record.attendance._id}/validate`,
          {}
        );
        await this.loadAttendance();
      } catch (err) {
        showApiError(this, err);
      }
    },
    async validateSessionDay() {
      if (!this.selectedDate) return;
      this.validateDayLoading = true;
      try {
        const body = {
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
        };
        if (this.selectedTeacher) body.teacher = this.selectedTeacher;
        await apiClient.post("/teacherAttendance/validate-day", body);
        await this.loadAttendance();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.validateDayLoading = false;
      }
    },
    async validatePersonnelAttendance(record) {
      if (!record.attendance || !record.attendance._id) return;
      try {
        await apiClient.post(
          `/personnelDailyAttendance/${record.attendance._id}/validate`,
          {}
        );
        await this.loadPersonnelDay();
      } catch (err) {
        showApiError(this, err);
      }
    },
    async validatePersonnelDay() {
      if (!this.personnelDate) return;
      this.validatePersonnelDayLoading = true;
      try {
        const body = {
          date: moment(this.personnelDate).format("YYYY-MM-DD"),
        };
        if (this.personnelFilterEmployee)
          body.employee = this.personnelFilterEmployee;
        await apiClient.post("/personnelDailyAttendance/validate-day", body);
        await this.loadPersonnelDay();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.validatePersonnelDayLoading = false;
      }
    },
    teacherName(t) {
      if (!t) return "";
      const emp = t.employee || {};
      return [emp.firstName, emp.lastName].filter(Boolean).join(" ") || t.email;
    },
    employeeName(e) {
      if (!e) return "";
      return (
        [e.firstName, e.lastName].filter(Boolean).join(" ") || e.cin || ""
      );
    },
    sessionLabel(s) {
      if (!s) return "";
      const sub = s.subject || s.moduleGeneral;
      return sub ? sub.name || "" : "";
    },
    statusColor(status) {
      if (status === "present") return "green";
      if (status === "absent") return "red";
      if (status === "late") return "orange";
      if (status === "absent_morning" || status === "absent_afternoon")
        return "volcano";
      return "default";
    },
    onTabChange(key) {
      if (key === "report" && !this.summaryRows.length) {
        this.loadSummary();
      }
      if (key === "personnelDaily") {
        this.loadPersonnelDay();
      }
      if (key === "report" && this.reportMode === "personnelDaily") {
        this.ensureReportPersonnelOptions();
      }
      if (key === "comparison" && !this.comparisonRows.length) {
        this.loadComparison();
      }
    },

    onReportModeChange() {
      this.summaryRows = [];
      this.summaryTotals = null;
      if (this.reportMode === "personnelDaily") {
        this.ensureReportPersonnelOptions();
      }
    },

    async ensureReportPersonnelOptions() {
      if (this.personnelEmployees.length) return;
      try {
        const { data } = await apiClient.post("/employees/filter", {
          status: "active",
        });
        this.personnelEmployees = data || [];
      } catch (err) {
        showApiError(this, err);
      }
    },

    async loadTeachers() {
      try {
        const { data } = await apiClient.post("/teachers/filter", {
          query: { status: "active" },
        });
        this.teachers = data;
      } catch (err) {
        showApiError(this, err);
      }
    },

    async loadPersonnelDay() {
      if (!this.personnelDate) return;
      this.personnelLoading = true;
      try {
        const { data } = await apiClient.post("/personnelDailyAttendance/day", {
          date: moment(this.personnelDate).format("YYYY-MM-DD"),
          includeDeleted: !!this.showDeletedPersonnel,
        });
        this.personnelRows = (data.rows || []).map((r) => ({
          ...r,
          rowKey: r.employee?._id || r.employee,
        }));
        this.personnelEmployees = this.personnelRows
          .map((r) => r.employee)
          .filter(Boolean);
        this.applyPersonnelFilter();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.personnelLoading = false;
      }
    },

    applyPersonnelFilter() {
      let rows = this.personnelRows.slice();
      if (this.personnelFilterEmployee) {
        rows = rows.filter(
          (r) => String(r.employee?._id) === String(this.personnelFilterEmployee)
        );
      }
      if (this.personnelFilterStatus) {
        rows = rows.filter((r) => {
          const status = r.attendance
            ? r.attendance.status
            : "present";
          // Unmarked defaults to present for filter purposes
          if (
            this.personnelFilterStatus === "present" &&
            !r.attendance
          ) {
            return true;
          }
          return status === this.personnelFilterStatus;
        });
      }
      this.personnelFilteredRows = rows;
    },

    async markPersonnelStatus(record, status, delayMinutes = null) {
      try {
        const body = {
          employee: record.employee._id,
          date: moment(this.personnelDate).format("YYYY-MM-DD"),
          status,
        };
        if (status === "late") body.delayMinutes = delayMinutes;
        const { data } = await apiClient.put(
          "/personnelDailyAttendance",
          body
        );
        const idx = this.personnelRows.findIndex(
          (r) => String(r.employee?._id) === String(record.employee._id)
        );
        if (idx >= 0) {
          this.$set(this.personnelRows, idx, {
            ...this.personnelRows[idx],
            attendance: data,
          });
        }
        this.applyPersonnelFilter();
        notification.success({
          message: this.$t("teacherAttendance.updated"),
        });
      } catch (err) {
        showApiError(this, err);
      }
    },

    openPersonnelLateModal(record) {
      this.lateMode = "personnel";
      this.lateRecord = record;
      this.delayMinutes = record.attendance?.delayMinutes || null;
      this.lateModalVisible = true;
    },

    closeLateModal() {
      this.lateModalVisible = false;
      this.lateRecord = null;
      this.delayMinutes = null;
      this.lateMode = "session";
    },

    async removePersonnelAttendance(record) {
      if (!record.attendance?._id) return;
      try {
        await apiClient.delete(
          `/personnelDailyAttendance/${record.attendance._id}`
        );
        await this.loadPersonnelDay();
        notification.success({
          message: this.$t("teacherAttendance.removed"),
        });
      } catch (err) {
        showApiError(this, err);
      }
    },

    async handleDateChange() {
      if (!this.selectedDate) return;
      this.loading = true;
      const start = moment(this.selectedDate).startOf("day").toISOString();
      const end = moment(this.selectedDate).endOf("day").toISOString();

      try {
        const { data: schedules } = await apiClient.post("/schedules/filter", {
          query: {
            day: { $gte: start, $lte: end },
            status: "active",
            canceled: false,
          },
        });
        this.schedules = schedules;
        await this.loadAttendance();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.loading = false;
      }
    },

    async loadAttendance() {
      if (!this.selectedDate) return;
      this.loading = true;
      try {
        const body = {
          date: moment(this.selectedDate).format("YYYY-MM-DD"),
          includeDeleted: !!this.showDeleted,
        };
        if (this.selectedTeacher) body.teacher = this.selectedTeacher;

        const { data: records } = await apiClient.post(
          "/teacherAttendance/filter",
          body
        );
        this.attendanceMap = {};
        records.forEach((r) => {
          const key = `${r.teacher?._id || r.teacher}_${
            r.schedule?._id || r.schedule
          }`;
          this.attendanceMap[key] = r;
        });
        this.buildTable();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.loading = false;
      }
    },

    buildTable() {
      let rows = [];
      let schedules = this.schedules;
      if (this.selectedTeacher) {
        schedules = schedules.filter((s) => {
          const tid =
            typeof s.teacher === "object" ? s.teacher._id : s.teacher;
          return tid === this.selectedTeacher;
        });
      }

      schedules.forEach((s) => {
        const tid = typeof s.teacher === "object" ? s.teacher._id : s.teacher;
        const teacher =
          typeof s.teacher === "object"
            ? s.teacher
            : this.teachers.find((t) => t._id === tid);
        const key = `${tid}_${s._id}`;
        rows.push({
          _id: `${s._id}_${tid}`,
          teacher,
          schedule: s,
          attendance: this.attendanceMap[key] || null,
        });
      });

      this.tableData = rows;
      this.applyFilter();
    },

    applyFilter() {
      if (!this.filterStatus) {
        this.filteredData = this.tableData;
        return;
      }
      this.filteredData = this.tableData.filter((r) => {
        if (
          this.filterStatus === "present" ||
          this.filterStatus === "absent" ||
          this.filterStatus === "late"
        ) {
          return r.attendance && r.attendance.status === this.filterStatus;
        }
        return true;
      });
    },

    async markStatus(record, status, delayMinutes = null) {
      try {
        if (record.attendance) {
          const body = { status };
          if (status === "late") body.delayMinutes = delayMinutes;
          const { data } = await apiClient.patch(
            `/teacherAttendance/${record.attendance._id}`,
            body
          );
          const key = `${data.teacher?._id || data.teacher}_${
            data.schedule?._id || data.schedule
          }`;
          this.$set(this.attendanceMap, key, data);
        } else {
          const tid =
            typeof record.teacher === "object"
              ? record.teacher._id
              : record.teacher;
          const body = {
            teacher: tid,
            schedule: record.schedule._id,
            date: moment(this.selectedDate).format("YYYY-MM-DD"),
            status,
          };
          if (status === "late") body.delayMinutes = delayMinutes;
          const { data } = await apiClient.put("/teacherAttendance", body);
          const key = `${data.teacher?._id || data.teacher}_${
            data.schedule?._id || data.schedule
          }`;
          this.$set(this.attendanceMap, key, data);
        }
        this.buildTable();
        notification.success({
          message: this.$t("teacherAttendance.updated"),
        });
      } catch (err) {
        showApiError(this, err);
      }
    },

    openLateModal(record) {
      this.lateMode = "session";
      this.lateRecord = record;
      this.delayMinutes = record.attendance?.delayMinutes || null;
      this.lateModalVisible = true;
    },

    async confirmLate() {
      if (!this.delayMinutes || !this.lateRecord) return;
      const record = this.lateRecord;
      const delay = this.delayMinutes;
      const mode = this.lateMode;
      this.closeLateModal();
      if (mode === "personnel") {
        await this.markPersonnelStatus(record, "late", delay);
      } else {
        await this.markStatus(record, "late", delay);
      }
    },

    async removeAttendance(record) {
      try {
        await apiClient.delete(`/teacherAttendance/${record.attendance._id}`);
        await this.loadAttendance();
        notification.success({
          message: this.$t("teacherAttendance.removed"),
        });
      } catch (err) {
        showApiError(this, err);
      }
    },

    async loadSummary() {
      if (!this.reportStart || !this.reportEnd) {
        notification.warning({
          message: this.$t("error.selectionnerDates"),
        });
        return;
      }
      this.reportLoading = true;
      try {
        if (this.reportMode === "personnelDaily") {
          const body = {
            startDate: moment(this.reportStart).format("YYYY-MM-DD"),
            endDate: moment(this.reportEnd).format("YYYY-MM-DD"),
          };
          if (this.reportEmployee) body.employee = this.reportEmployee;

          const { data } = await apiClient.post(
            "/personnelDailyAttendance/summary",
            body
          );
          this.summaryRows = data.employees || [];
          this.summaryTotals = data.totals || null;
        } else {
          const body = {
            startDate: moment(this.reportStart).format("YYYY-MM-DD"),
            endDate: moment(this.reportEnd).format("YYYY-MM-DD"),
          };
          if (this.reportTeacher) body.teacher = this.reportTeacher;

          const { data } = await apiClient.post(
            "/teacherAttendance/summary",
            body
          );
          this.summaryRows = data.teachers || [];
          this.summaryTotals = data.totals || null;
        }
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.reportLoading = false;
      }
    },

    exportPdf() {
      if (!this.summaryRows.length) return;
      this.pdfLoading = true;
      try {
        const period = `${moment(this.reportStart).format("DD/MM/YYYY")} - ${moment(
          this.reportEnd
        ).format("DD/MM/YYYY")}`;
        if (this.reportMode === "personnelDaily") {
          generatePersonnelDailyAttendanceSummaryPdf({
            settings: this.settings,
            locale: this.$i18n.locale,
            t: this.$t.bind(this),
            title: this.$t(
              "teacherAttendance.personnelDailyReportTitle"
            ),
            period,
            employees: this.summaryRows,
            totals: this.summaryTotals || {
              present: 0,
              absent: 0,
              late: 0,
              absentMorning: 0,
              absentAfternoon: 0,
              delayMinutes: 0,
              markedDays: 0,
            },
            gtag: this.$gtag,
          });
        } else {
          generateTeacherAttendanceSummaryPdf({
            settings: this.settings,
            locale: this.$i18n.locale,
            t: this.$t.bind(this),
            period,
            teachers: this.summaryRows,
            totals: this.summaryTotals || {
              present: 0,
              absent: 0,
              late: 0,
              delayMinutes: 0,
              workedHours: 0,
              workedDays: 0,
              offSite: 0,
            },
            gtag: this.$gtag,
          });
        }
      } finally {
        this.pdfLoading = false;
      }
    },

    async loadComparison() {
      if (!this.comparisonStart || !this.comparisonEnd) {
        notification.warning({
          message: this.$t("error.selectionnerDates"),
        });
        return;
      }
      this.comparisonLoading = true;
      try {
        const { data } = await apiClient.post(
          "/personnelDailyAttendance/comparison",
          {
            startDate: moment(this.comparisonStart).format("YYYY-MM-DD"),
            endDate: moment(this.comparisonEnd).format("YYYY-MM-DD"),
          }
        );
        this.comparisonRows = data.rows || [];
        this.comparisonTotals = data.totals || null;
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.comparisonLoading = false;
      }
    },

    comparisonExpandIcon(props) {
      if (!props.record.bySubject || !props.record.bySubject.length) {
        return null;
      }
      const h = this.$createElement;
      return h("a-icon", {
        props: {
          type: props.expanded ? "minus-square" : "plus-square",
        },
        style: { cursor: "pointer", marginRight: "8px" },
        on: {
          click: (e) => {
            e.stopPropagation();
            props.onExpand(props.record, e);
          },
        },
      });
    },

    exportComparisonPdf() {
      if (!this.filteredComparisonRows.length) return;
      this.comparisonPdfLoading = true;
      try {
        const period = `${moment(this.comparisonStart).format(
          "DD/MM/YYYY"
        )} - ${moment(this.comparisonEnd).format("DD/MM/YYYY")}`;
        generateAttendanceComparisonPdf({
          settings: this.settings,
          locale: this.$i18n.locale,
          t: this.$t.bind(this),
          title: this.$t("teacherAttendance.comparisonTitle"),
          period,
          rows: this.filteredComparisonRows,
          totals: this.filteredComparisonTotals || {
            expectedHours: 0,
            actualHours: 0,
            absentHours: 0,
            delayHours: 0,
            varianceHours: 0,
          },
          gtag: this.$gtag,
        });
      } finally {
        this.comparisonPdfLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.filter-label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}
.stat-box {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
}
.stat-label {
  font-size: 12px;
  color: #666;
}
.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #1890ff;
}
.mb-3 {
  margin-bottom: 16px;
}
</style>
